import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, GripVertical, Search } from 'lucide-react'
import { menusApi, pagesApi } from '../../services/api'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Modal from '../../components/ui/Modal'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'
import { titleToPageSlug } from '../../utils/pageSlug'
import {
  STORY_CARD_MENU_OPTIONS,
  STORY_CARD_PAGE_SLUGS,
  storyCardLinkValue,
  storyCardTypeForPageSlug,
  storyCardTypeFromLinkValue,
  isStoryCardLinkValue,
} from '../../constants/storyCards'

const EMPTY = {
  title_en: '',
  title_ar: '',
  slug: '',
  url: '',
  tab: '',
  sort_order: 0,
  is_active: true,
  parent_id: '',
  page_id: '',
  link_value: '',
  story_card_type: '',
}

const OTHER_SITE_LINKS = [
  { url: '/programs', slug: 'Programs' },
  { url: '/news', slug: 'News' },
  { url: '/events', slug: 'Events' },
  { url: '/apply', slug: 'Apply' },
  { url: '/careers', slug: 'Careers' },
  { url: '/gallery', slug: 'Gallery' },
]

function buildMenuPayload(form) {
  return {
    title_en: form.title_en,
    title_ar: form.title_ar,
    slug: form.slug,
    url: form.url || null,
    tab: form.tab || null,
    parent_id: form.parent_id ? Number(form.parent_id) : null,
    page_id: form.page_id ? Number(form.page_id) : null,
    story_card_type: form.story_card_type || null,
    sort_order: Number(form.sort_order || 0),
    is_active: Boolean(form.is_active),
  }
}

function itemToPayload(item, sortOrder = item.sort_order) {
  return buildMenuPayload({
    title_en: item.title_en,
    title_ar: item.title_ar,
    slug: item.slug,
    url: item.url ?? '',
    tab: item.tab ?? '',
    parent_id: item.parent_id ?? '',
    page_id: item.page_id ?? '',
    story_card_type: item.story_card_type ?? '',
    sort_order: sortOrder,
    is_active: item.is_active,
  })
}

function compareMenuOrder(a, b) {
  const aActive = a.is_active !== false ? 0 : 1
  const bActive = b.is_active !== false ? 0 : 1
  if (aActive !== bActive) return aActive - bActive
  return (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
}

function siblingGroupKey(parentId) {
  return parentId ?? 'root'
}

function resolveInactiveSortOrder(form, siblingGroups) {
  if (form.is_active !== false) {
    return Number(form.sort_order || 0)
  }

  const key = form.parent_id ? Number(form.parent_id) : 'root'
  const siblings = (siblingGroups.get(key) || []).filter((entry) => entry.id !== form.id)
  const maxOrder = siblings.reduce((max, entry) => Math.max(max, entry.sort_order ?? 0), 0)

  return maxOrder + 1
}

function flattenMenuTree(items, depth = 0) {
  if (!Array.isArray(items)) return []

  const sorted = [...items].sort(compareMenuOrder)

  return sorted.flatMap((item) => {
    const row = { ...item, depth }
    const children = flattenMenuTree(item.children || [], depth + 1)
    return [row, ...children]
  })
}

function groupSiblings(rows) {
  const groups = new Map()

  rows.forEach((row) => {
    const key = siblingGroupKey(row.parent_id)
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(row)
  })

  for (const list of groups.values()) {
    list.sort(compareMenuOrder)
  }

  return groups
}

function menuItemLink(item) {
  if (item.url) return item.url
  if (item.page?.slug) return `/pages/${item.page.slug}`
  if (item.story_card_type && STORY_CARD_PAGE_SLUGS[item.story_card_type]) {
    return `/pages/${STORY_CARD_PAGE_SLUGS[item.story_card_type]}`
  }
  return ''
}

function resolveStoryCardLink(type, pages) {
  const slug = STORY_CARD_PAGE_SLUGS[type]
  if (!slug) return { story_card_type: '', page_id: '', url: '', slug: '' }

  const page = pages.find((entry) => entry.slug === slug)

  return {
    story_card_type: type,
    page_id: page?.id ?? '',
    url: page?.id ? '' : `/pages/${slug}`,
    slug: page?.slug || slug,
  }
}

function matchesMenuSearch(item, query) {
  const q = query.trim().toLowerCase()
  if (!q) return true

  const haystack = [
    item.title_en,
    item.title_ar,
    item.slug,
    item.url,
    item.tab,
    item.page?.slug,
    item.page?.title_en,
    menuItemLink(item),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return haystack.includes(q)
}

function linkValueFromForm(form, pages) {
  if (form.story_card_type) {
    return storyCardLinkValue(form.story_card_type)
  }

  if (form.page_id) {
    const page = pages.find((entry) => entry.id === Number(form.page_id))
    const storyType = page ? storyCardTypeForPageSlug(page.slug) : null
    if (storyType) {
      return storyCardLinkValue(storyType)
    }
    return `page:${form.page_id}`
  }

  if (form.url) {
    const pageFromUrl = pages.find((page) => form.url === `/pages/${page.slug}`)
    if (pageFromUrl) {
      return `page:${pageFromUrl.id}`
    }

    const known = OTHER_SITE_LINKS.find((entry) => entry.url === form.url)
    if (known) {
      return `route:${known.url}`
    }

    return `route:${form.url}`
  }

  return ''
}

function applyLinkSelection(form, linkValue, pages) {
  if (isStoryCardLinkValue(linkValue)) {
    const type = storyCardTypeFromLinkValue(linkValue)
    const resolved = resolveStoryCardLink(type, pages)

    return {
      ...form,
      link_value: linkValue,
      ...resolved,
    }
  }

  if (linkValue.startsWith('page:')) {
    const pageId = Number(linkValue.slice(5))
    const page = pages.find((entry) => entry.id === pageId)

    return {
      ...form,
      link_value: linkValue,
      page_id: pageId,
      url: '',
      story_card_type: '',
      slug: page?.slug || form.slug,
    }
  }

  if (linkValue.startsWith('route:')) {
    const url = linkValue.slice(6)
    const known = OTHER_SITE_LINKS.find((entry) => entry.url === url)

    return {
      ...form,
      link_value: linkValue,
      page_id: '',
      url,
      story_card_type: '',
      slug: known?.slug || titleToPageSlug(form.title_en),
    }
  }

  return {
    ...form,
    link_value: '',
    page_id: '',
    url: '',
    story_card_type: '',
    slug: titleToPageSlug(form.title_en),
  }
}

function formFromMenuItem(item, pages) {
  const base = {
    ...item,
    parent_id: item.parent_id ?? '',
    page_id: item.page_id ?? '',
    tab: item.tab ?? '',
    url: item.url ?? '',
    story_card_type: item.story_card_type ?? '',
  }

  if (base.story_card_type) {
    const resolved = resolveStoryCardLink(base.story_card_type, pages)
    base.page_id = base.page_id || resolved.page_id
    base.url = base.url || resolved.url
    base.slug = base.slug || resolved.slug
  }

  return {
    ...base,
    link_value: linkValueFromForm(base, pages),
  }
}

function prepareFormForSave(form, pages) {
  if (!isStoryCardLinkValue(form.link_value)) {
    return form
  }

  const type = storyCardTypeFromLinkValue(form.link_value)
  const resolved = resolveStoryCardLink(type, pages)

  return {
    ...form,
    ...resolved,
    link_value: form.link_value,
  }
}

export default function AdminMenus() {
  const { t } = useTranslation()
  const [tree, setTree] = useState([])
  const [pages, setPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [reordering, setReordering] = useState(false)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const rows = useMemo(() => flattenMenuTree(tree), [tree])
  const siblingGroups = useMemo(() => groupSiblings(rows), [rows])

  const displayRows = useMemo(() => {
    if (!searchQuery) return rows
    return rows.filter((item) => matchesMenuSearch(item, searchQuery))
  }, [rows, searchQuery])

  const sortedPages = useMemo(
    () => [...pages].sort((a, b) => (a.title_en || '').localeCompare(b.title_en || '')),
    [pages],
  )

  const parentOptions = useMemo(() => {
    if (!form) return rows

    return rows.filter((row) => row.id !== form.id)
  }, [rows, form])

  const load = () => {
    setLoading(true)
    menusApi
      .getAllAdmin()
      .then(({ data }) => setTree(data.data || data || []))
      .catch(() => setTree([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    pagesApi
      .getAllAdmin({ per_page: 500 })
      .then(({ data }) => setPages(data.data || data || []))
      .catch(() => setPages([]))
  }, [])

  const getSiblingMeta = (item) => {
    const key = siblingGroupKey(item.parent_id)
    const siblings = siblingGroups.get(key) || []
    const index = siblings.findIndex((entry) => entry.id === item.id)
    const activeCount = siblings.filter((entry) => entry.is_active !== false).length
    const isActive = item.is_active !== false

    return {
      index,
      canMoveUp: isActive ? index > 0 : index > activeCount,
      canMoveDown: isActive ? index < activeCount - 1 : index >= 0 && index < siblings.length - 1,
      siblings,
      activeCount,
    }
  }

  const moveItem = async (item, direction) => {
    const { index, siblings, canMoveUp, canMoveDown } = getSiblingMeta(item)
    if (direction === 'up' && !canMoveUp) return
    if (direction === 'down' && !canMoveDown) return

    const targetIndex = direction === 'up' ? index - 1 : index + 1
    const target = siblings[targetIndex]
    if (!target) return

    // Keep active items above disabled ones.
    if (item.is_active !== false && target.is_active === false) return
    if (item.is_active === false && target.is_active !== false) return

    const reordered = [...siblings]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(targetIndex, 0, moved)

    setReordering(true)
    try {
      await Promise.all(
        reordered.map((entry, orderIndex) =>
          menusApi.update(entry.id, itemToPayload(entry, orderIndex + 1)),
        ),
      )
      load()
    } catch {
      window.alert(t('admin.menus.reorderFailed'))
    } finally {
      setReordering(false)
    }
  }

  const updateFormField = (key, value) => {
    setForm((current) => {
      if (!current) return current

      if (key === 'title_en') {
        const next = { ...current, title_en: value }
        if (!current.page_id && !current.url) {
          next.slug = titleToPageSlug(value)
        }
        return next
      }

      return { ...current, [key]: value }
    })
  }

  const handleLinkChange = (linkValue) => {
    setForm((current) => (current ? applyLinkSelection(current, linkValue, sortedPages) : current))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSaveError('')
    try {
      const prepared = prepareFormForSave(form, sortedPages)
      const payload = buildMenuPayload({
        ...prepared,
        sort_order: resolveInactiveSortOrder(prepared, siblingGroups),
      })
      if (form.id) await menusApi.update(form.id, payload)
      else await menusApi.create(payload)
      setForm(null)
      load()
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        Object.values(err?.response?.data?.errors || {})?.flat?.()?.[0] ||
        'Failed to save'
      setSaveError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.menus.confirmDelete'))) return
    await menusApi.delete(id)
    load()
  }

  const openCreate = () => {
    setSaveError('')
    setForm({ ...EMPTY })
  }

  const openEdit = (item) => {
    setSaveError('')
    setForm(formFromMenuItem(item, sortedPages))
  }

  const closeForm = () => {
    setSaveError('')
    setForm(null)
  }

  const selectClass = `${adminInputClass} bg-white`

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-primary">{t('admin.menus.title')}</h1>
        <Button onClick={openCreate} variant="accent" size="sm">
          <Plus className="h-4 w-4" />
          {t('admin.menus.add')}
        </Button>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text/40" />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('admin.menus.searchMenus')}
          aria-label={t('admin.menus.searchMenus')}
          className="w-full rounded-xl border border-border bg-white py-2.5 ps-10 pe-4 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {searchQuery ? (
        <p className="mb-4 text-sm text-text/60">
          {t('admin.menus.searchResults', { count: displayRows.length, query: searchQuery })}
        </p>
      ) : null}

      <Modal
        isOpen={Boolean(form)}
        onClose={closeForm}
        title={form?.id ? t('admin.menus.edit') : t('admin.menus.add')}
      >
        {form && (
          <form onSubmit={handleSave} className="grid gap-4 sm:grid-cols-2">
            <AdminFormField label={t('admin.menus.titleEn')} htmlFor="menu-title-en">
              <input
                id="menu-title-en"
                value={form.title_en ?? ''}
                onChange={(e) => updateFormField('title_en', e.target.value)}
                required
                className={adminInputClass}
              />
            </AdminFormField>

            <AdminFormField label={t('admin.menus.titleAr')} htmlFor="menu-title-ar">
              <input
                id="menu-title-ar"
                value={form.title_ar ?? ''}
                onChange={(e) => updateFormField('title_ar', e.target.value)}
                required
                dir="rtl"
                lang="ar"
                className={`${adminInputClass} font-ar`}
              />
            </AdminFormField>

            <AdminFormField
              label={t('admin.menus.linkedPage')}
              htmlFor="menu-linked-page"
              className="sm:col-span-2"
            >
              <select
                id="menu-linked-page"
                value={form.link_value ?? linkValueFromForm(form, sortedPages)}
                onChange={(e) => handleLinkChange(e.target.value)}
                className={selectClass}
              >
                <option value="">{t('admin.menus.linkedPageNone')}</option>
                <optgroup label={t('admin.menus.pagesGroup')}>
                  {sortedPages.map((page) => (
                    <option key={page.id} value={`page:${page.id}`}>
                      {page.title_en} ({page.slug})
                    </option>
                  ))}
                </optgroup>
                <optgroup label={t('admin.menus.otherLinksGroup')}>
                  {OTHER_SITE_LINKS.map((entry) => (
                    <option key={entry.url} value={`route:${entry.url}`}>
                      {entry.url}
                    </option>
                  ))}
                </optgroup>
                <optgroup label={t('admin.menus.storyCardsGroup')}>
                  {STORY_CARD_MENU_OPTIONS.map(({ type, labelKey }) => (
                    <option key={type} value={storyCardLinkValue(type)}>
                      {t(labelKey)}
                    </option>
                  ))}
                </optgroup>
              </select>
            </AdminFormField>

            <AdminFormField label={t('admin.menus.slug')} htmlFor="menu-slug" className="sm:col-span-2">
              <input
                id="menu-slug"
                value={form.slug ?? ''}
                readOnly
                className={`${adminInputClass} cursor-default bg-background text-text/70`}
              />
            </AdminFormField>

            <AdminFormField label={t('admin.menus.parentMenu')} htmlFor="menu-parent">
              <select
                id="menu-parent"
                value={form.parent_id ?? ''}
                onChange={(e) => updateFormField('parent_id', e.target.value)}
                className={selectClass}
              >
                <option value="">{t('admin.menus.parentMenuNone')}</option>
                {parentOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {'—'.repeat(item.depth)} {item.title_en}
                  </option>
                ))}
              </select>
            </AdminFormField>

            <AdminFormField label={t('admin.menus.tab')} htmlFor="menu-tab">
              <input
                id="menu-tab"
                value={form.tab ?? ''}
                onChange={(e) => updateFormField('tab', e.target.value)}
                className={adminInputClass}
              />
            </AdminFormField>

            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={Boolean(form.is_active)}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              {t('admin.menus.active')}
            </label>

            <div className="flex flex-wrap gap-2 sm:col-span-2">
              <Button type="submit" variant="primary" disabled={saving}>
                {t('buttons.save')}
              </Button>
              <Button type="button" variant="ghost" onClick={closeForm}>
                {t('buttons.cancel')}
              </Button>
            </div>
            {saveError && <p className="text-sm text-red-600 sm:col-span-2">{saveError}</p>}
          </form>
        )}
      </Modal>

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : displayRows.length === 0 ? (
        <p className="text-text/60">
          {searchQuery ? t('admin.menus.searchNoResults') : t('admin.table.noData')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-background">
              <tr>
                <th className="w-28 px-3 py-3 text-start">{t('admin.menus.order')}</th>
                <th className="px-4 py-3 text-start">{t('admin.menus.titleEn')}</th>
                <th className="px-4 py-3 text-start">{t('admin.menus.titleAr')}</th>
                <th className="px-4 py-3 text-start">{t('admin.menus.link')}</th>
                <th className="px-4 py-3 text-end">{t('admin.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((item, searchIndex) => {
                const { index, canMoveUp, canMoveDown } = getSiblingMeta(item)
                const displayOrder = searchQuery ? searchIndex + 1 : index >= 0 ? index + 1 : item.sort_order
                const reorderDisabled = Boolean(searchQuery) || reordering

                return (
                  <tr key={item.id} className="border-b border-border">
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <GripVertical className="h-4 w-4 shrink-0 text-text/30" aria-hidden="true" />
                        <span className="min-w-[1.5rem] font-semibold tabular-nums text-primary">
                          {displayOrder}
                        </span>
                        {!searchQuery ? (
                          <div className="flex flex-col">
                            <button
                              type="button"
                              disabled={reorderDisabled || !canMoveUp}
                              onClick={() => moveItem(item, 'up')}
                              className="rounded p-0.5 text-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-30"
                              aria-label={t('admin.menus.moveUp')}
                              title={t('admin.menus.moveUp')}
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              disabled={reorderDisabled || !canMoveDown}
                              onClick={() => moveItem(item, 'down')}
                              className="rounded p-0.5 text-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-30"
                              aria-label={t('admin.menus.moveDown')}
                              title={t('admin.menus.moveDown')}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className="flex items-center gap-2"
                        style={{ paddingInlineStart: `${item.depth * 1.25}rem` }}
                      >
                        {item.depth > 0 ? (
                          <span className="text-text/30" aria-hidden="true">
                            └
                          </span>
                        ) : null}
                        <span className={item.is_active ? 'text-text' : 'text-text/40 line-through'}>
                          {item.title_en}
                        </span>
                        {!item.is_active ? (
                          <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-600">
                            {t('admin.homepage.inactive')}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-ar" lang="ar" dir="rtl">
                      {item.title_ar}
                    </td>
                    <td className="px-4 py-3">
                      {menuItemLink(item) || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(item)}
                          className="rounded-lg p-2 text-primary hover:bg-primary/5"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
